import React, { useState, useMemo, useEffect, useRef } from "react";
import { PlusIcon } from "@heroicons/react/solid";
import { IconButton, toast } from "ui";
import Loading from "../../components/Loading";
import { RoutingResource } from "../../features/routing/services/routing.service";
import Drawer from "../../components/Drawer";
import DrawerFlowFormContent from "../../components/PaymentFlows/DrawerFlowFormContent";
import { axiosApiInstance } from "../api/axios";
import FlowCard from "../../components/PaymentFlows/FlowCard";
import { Filters } from "../../components/newFilters";

import NoResults from "../../components/NoResults/NoResults";
import Layout from "../../components/Layout";
import { Pagination } from "../../components/Pagination";
import { useMoneyHashQuery } from "../../hooks/useMoneyHashQuery";
import { useRestoreScrollOnFetching } from "../../hooks/useRestoreScrollOnFetching";
import { useRouter } from "next/router";
import { useGlobalSearchQuery } from "../../components/GlobalSearch";
import { usePaginationParams } from "../../hooks/usePaginationParams";
import { ErrorPage } from "../../components/ErrorPage";
import { PageHeader } from "../../components/PageHeading";
import { FlowsNavigationTabs } from "../../components/NavigationTabs/FlowsNavigationTabs";
import { useAnalytics } from "../../features/analytics/analyticsProvider";

export default function FlowsPage() {
  const router = useRouter();
  const [showCreateFlow, setShowCreateFlow] = useState(false);
  const searchQuery = useGlobalSearchQuery();
  const [paginationParams] = usePaginationParams();
  const { analytics } = useAnalytics();
  const isTrackedRef = useRef(false);

  const {
    data: flows,
    count,
    listMeta,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useMoneyHashQuery({
    queryKey: ["flows", { ...paginationParams, ...router.query }],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get("/d/flows/", {
          params: {
            ...paginationParams,
            ...router.query,
          },
          signal,
        })
        .then((res) => res.data),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (count === undefined || isTrackedRef.current) return;
    analytics.track("flows_list_viewed", {
      total_flows: count,
    });
    isTrackedRef.current = true;
  }, [count, analytics]);

  const scrollRef = useRestoreScrollOnFetching(isFetching);

  const isFiltering = useMemo(
    () =>
      Boolean(
        searchQuery ||
          (listMeta?.filter_enabled &&
            listMeta.filter_types.some(
              ({ field_name }) => !!router.query[field_name]
            ))
      ),
    [searchQuery, listMeta, router.query]
  );

  return (
    <Layout
      ref={scrollRef}
      metaTitle="Payment Flows"
      className="flex flex-col gap-4"
    >
      <PageHeader
        title="Manage your payment flows"
        description="Create workflows combining everything you need and assign it to multiple accounts."
        withoutBorder
        actions={
          listMeta?.add_enabled && (
            <IconButton
              data-testid="create-flow-button"
              onClick={() => setShowCreateFlow(true)}
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
            </IconButton>
          )
        }
      />
      <FlowsNavigationTabs activeTab="flows" />

      <Drawer
        title="New Payment Flow"
        description="Create workflows combining everything you need and assign it to multiple accounts."
        isOpen={showCreateFlow}
        onClose={() => setShowCreateFlow(false)}
        size="max-w-lg"
      >
        <DrawerFlowFormContent
          isOpen={showCreateFlow}
          onClose={() => setShowCreateFlow(false)}
          onSubmit={RoutingResource.createFlow}
          onSuccess={(flow) => {
            toast.success("Successfully created a new flow", {
              id: "flow",
              duration: 4000,
            });

            analytics.track("routing_create_flow", {
              flow_id: flow.id,
              name: flow.name,
            });

            router.push(`/flows/${flow.id}/routing?created=true`);
          }}
          flowId={null}
        />
      </Drawer>

      {isError && <ErrorPage error={error} />}

      {!isError && (
        <>
          {isLoading && !flows && (
            <span className="pt-6">
              <Loading />
            </span>
          )}

          {flows && (
            <>
              <h2 className="text-sm font-medium text-foundations-subtle">
                {flows?.length ? (
                  <>
                    {flows?.length} of {count} payment flows
                  </>
                ) : (
                  <>{flows?.length} payment flows</>
                )}
              </h2>

              {(listMeta?.filter_enabled || listMeta?.search_enabled) && (
                <Filters
                  enabledFilters={listMeta?.filter_enabled}
                  filters={listMeta.filter_types}
                  recentStoreKey="flows"
                  search={{
                    enabled: !!listMeta?.search_enabled,
                    placeholder: "Search Flows",
                  }}
                />
              )}

              <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {flows?.map((flow) => (
                  <FlowCard key={flow.id} flow={flow} onUpdate={refetch} />
                ))}
              </div>

              <Pagination
                total={count}
                onPageChange={({ _, page, limit }) => {
                  router.replace({
                    query: { ...router.query, page, limit },
                  });
                }}
              />
            </>
          )}

          {!isLoading && flows?.length === 0 && (
            <NoResults
              tableName="payment flows"
              isFiltering={isFiltering}
              removeAllFilters={() => {
                router.replace({ query: { page: 1 } });
              }}
            />
          )}
        </>
      )}
    </Layout>
  );
}
FlowsPage.parentPath = "/flows";
